import { useEffect, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

export function useFetch(url, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(url);
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, ...deps]);
  useEffect(() => {
    load();
  }, [load]);
  return { data, loading, reload: load, setData };
}

export const PageHeader = ({ title, subtitle, actions, eyebrow }) => (
  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8">
    <div>
      {eyebrow && <p className="text-[11px] uppercase tracking-[0.2em] text-[#10B981] mb-2">{eyebrow}</p>}
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white" data-testid="page-title">{title}</h1>
      {subtitle && <p className="mt-1.5 text-sm text-slate-400">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const StatCard = ({ label, value, icon: Icon, testid, sub, accent }) => (
  <div className="nx-card p-5 relative overflow-hidden transition-colors duration-200 hover:border-[#10B981]/40" data-testid={testid}>
    <div className="flex items-start justify-between">
      <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">{label}</p>
      {Icon && (
        <span className={`rounded-md p-1.5 ${accent ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#1A2232] text-slate-400"}`}>
          <Icon size={15} />
        </span>
      )}
    </div>
    <p className={`num mt-3 text-2xl font-semibold ${accent ? "text-[#10B981]" : "text-white"}`} data-testid={`${testid}-value`}>{value}</p>
    {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
  </div>
);

const STATUS = {
  approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  unlocked: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  pending: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  locked: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  awaiting_payment: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  rejected: "bg-red-500/10 text-red-400 border-red-500/30",
  suspended: "bg-red-500/10 text-red-400 border-red-500/30",
  cancelled: "bg-slate-500/10 text-slate-400 border-slate-500/30",
};

export const StatusBadge = ({ status, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${STATUS[status] || STATUS.cancelled}`}>
    {(status || "").replace("_", " ")}
  </span>
);

export const Panel = ({ title, action, children, className = "", testid }) => (
  <div className={`nx-card ${className}`} data-testid={testid}>
    {title && (
      <div className="flex items-center justify-between border-b border-[#1E2738] px-5 py-4">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        {action}
      </div>
    )}
    {children}
  </div>
);

export const Loading = () => (
  <div className="flex items-center justify-center py-16 text-slate-500" data-testid="loading">
    <Loader2 className="animate-spin" size={22} />
  </div>
);

export const Empty = ({ text = "Nothing here yet", cols }) =>
  cols ? (
    <tr><td colSpan={cols} className="px-4 py-12 text-center text-sm text-slate-500">{text}</td></tr>
  ) : (
    <div className="px-4 py-12 text-center text-sm text-slate-500">{text}</div>
  );

export const Table = ({ head, children, testid }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="w-full">
      <thead className="border-b border-[#1E2738] bg-[#0F141D]">
        <tr>{head.map((h) => <th key={h} className="nx-th">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-[#1E2738]">{children}</tbody>
    </table>
  </div>
);

export const FilterTabs = ({ value, onChange, options, testid }) => (
  <div className="inline-flex rounded-lg border border-[#242F42] bg-[#0B0E14] p-1" data-testid={testid}>
    {options.map(([v, l]) => (
      <button key={v} onClick={() => onChange(v)} data-testid={`${testid}-${v || "all"}`}
        className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${value === v ? "bg-[#10B981] text-slate-950" : "text-slate-400 hover:text-white"}`}>
        {l}
      </button>
    ))}
  </div>
);
